/**
 * The flow definition as the editor holds it — the document `server/nova/flows/definition.py`
 * validates (trigger · target · conditions · steps or phases · cooldown). The server returns it
 * as a JSON object; `normalise()` turns whatever arrives (a saved flow, a template, an AI draft)
 * into this shape, and `toServer()` drops what does not apply to a step's type.
 */

export type Op = '=' | '!=' | '>' | '>=' | '<' | '<=' | 'in' | 'not_in' | 'contains' | 'exists';
export const OPS: Op[] = ['=', '!=', '>', '>=', '<', '<=', 'in', 'not_in', 'contains', 'exists'];

export interface Condition {
  field: string;
  op: Op;
  value?: unknown;
}

export type TargetScope = 'all' | 'groups' | 'vehicles' | 'drivers' | 'assets';
export const TARGET_SCOPES: TargetScope[] = ['all', 'groups', 'vehicles', 'drivers', 'assets'];

export interface Target {
  scope: TargetScope;
  ids: string[];
  /** Empty = every project of the company. */
  project_ids: string[];
}

export type StepType = 'action' | 'wait' | 'delay' | 'approval' | 'task' | 'ai' | 'branch';
export const STEP_TYPES: StepType[] = ['action', 'wait', 'delay', 'approval', 'task', 'ai', 'branch'];

export interface Step {
  id: string;
  type: StepType;
  label?: string;
  // action
  action?: string | null;
  with?: Record<string, unknown>;
  // wait
  event?: string | null;
  match?: Condition[];
  timeout_s?: number | null;
  on_timeout?: 'continue' | 'fail' | 'goto';
  timeout_goto?: string | null;
  // delay
  seconds?: number | null;
  // approval / task
  permission?: string | null;
  /** "user:<id>" (the engine assigns only people by id). */
  assignee?: string | null;
  title?: string | null;
  due_s?: number | null;
  evidence_event?: string | null;
  // ai (also the message to an approver / the task description)
  prompt?: string | null;
  output?: 'text' | 'decision';
  // branch
  if?: Condition[];
  then?: string | null;
  else?: string | null;
}

export interface Phase {
  id: string;
  label: string;
  steps: Step[];
}

export interface Cooldown {
  per: 'subject' | 'flow';
  seconds: number;
}

/** "On a schedule" (trigger `flows.schedule`): local times, days, and whom one run is for. */
export const SCHEDULE_TRIGGER = 'flows.schedule';
export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type Weekday = (typeof WEEKDAYS)[number];
export type SchedulePer = 'flow' | 'driver' | 'vehicle';

export interface Schedule {
  /** Local times HH:MM (ignored when `every_minutes` is set). */
  at: string[];
  every_minutes: number | null;
  /** Empty = every day. */
  weekdays: Weekday[];
  /** Days of the month 1–31; empty = every day. */
  month_days: number[];
  /** IANA time zone; null = the company's. */
  timezone: string | null;
  /** One run for the flow, or one for each driver / vehicle of the target. */
  per: SchedulePer;
}

export function defaultSchedule(): Schedule {
  return { at: ['08:00'], every_minutes: null, weekdays: ['mon'], month_days: [], timezone: null, per: 'flow' };
}

export interface FlowDef {
  trigger: { type: string; where: Condition[]; schedule?: Schedule | null };
  target: Target;
  conditions: Condition[];
  steps: Step[];
  /** Case flows: steps grouped in phases. When phases exist, `steps` is empty. */
  phases: Phase[];
  cooldown: Cooldown;
}

export const STEP_ID_RE = /^[a-z][a-z0-9_]{0,39}$/;

const obj = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

function condition(v: unknown): Condition {
  const o = obj(v);
  const op = (OPS as string[]).includes(String(o.op)) ? (o.op as Op) : '=';
  return { field: String(o.field ?? ''), op, value: o.value ?? null };
}

function step(v: unknown, i: number): Step {
  const o = obj(v);
  const type = (STEP_TYPES as string[]).includes(String(o.type)) ? (o.type as StepType) : 'action';
  const s: Step = { id: str(o.id) ?? `step_${i + 1}`, type, label: str(o.label) ?? '' };
  if (type === 'action') {
    s.action = str(o.action);
    s.with = { ...obj(o.with ?? o.with_) };
  } else if (type === 'wait') {
    s.event = str(o.event);
    s.match = arr(o.match).map(condition);
    s.timeout_s = num(o.timeout_s);
    s.on_timeout = o.on_timeout === 'fail' || o.on_timeout === 'goto' ? o.on_timeout : 'continue';
    s.timeout_goto = str(o.timeout_goto);
  } else if (type === 'delay') {
    s.seconds = num(o.seconds);
  } else if (type === 'approval' || type === 'task') {
    s.title = str(o.title);
    s.prompt = str(o.prompt);
    s.permission = str(o.permission);
    s.assignee = str(o.assignee);
    s.due_s = num(o.due_s);
    if (type === 'task') s.evidence_event = str(o.evidence_event);
  } else if (type === 'ai') {
    s.prompt = str(o.prompt);
    s.output = o.output === 'decision' ? 'decision' : 'text';
  } else if (type === 'branch') {
    s.if = arr(o.if ?? o.if_).map(condition);
    s.then = str(o.then);
    s.else = str(o.else ?? o.else_);
  }
  return s;
}

function schedule(v: unknown): Schedule | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const per = o.per === 'driver' || o.per === 'vehicle' ? o.per : 'flow';
  return {
    at: arr(o.at).map(String),
    every_minutes: num(o.every_minutes),
    weekdays: arr(o.weekdays).filter((d): d is Weekday => (WEEKDAYS as readonly unknown[]).includes(d)),
    month_days: arr(o.month_days).filter((d): d is number => typeof d === 'number'),
    timezone: str(o.timezone),
    per,
  };
}

/** The stored schedule: only what is set (the server fills the defaults). */
export function scheduleToServer(s: Schedule): Record<string, unknown> {
  const out: Record<string, unknown> = { per: s.per };
  if (s.every_minutes) out.every_minutes = s.every_minutes;
  else out.at = s.at;
  if (s.weekdays.length) out.weekdays = s.weekdays;
  if (s.month_days.length) out.month_days = s.month_days;
  if (s.timezone) out.timezone = s.timezone;
  return out;
}

/** Any definition-like JSON → a complete FlowDef the editor can bind to. */
export function normalise(raw: unknown): FlowDef {
  const o = obj(raw);
  const trig = obj(o.trigger);
  const target = obj(o.target);
  const scope = (TARGET_SCOPES as string[]).includes(String(target.scope)) ? (target.scope as TargetScope) : 'all';
  const cooldown = obj(o.cooldown);
  const phases = arr(o.phases).map((p, pi) => {
    const po = obj(p);
    return { id: str(po.id) ?? `phase_${pi + 1}`, label: str(po.label) ?? '', steps: arr(po.steps).map(step) };
  });
  return {
    trigger: {
      type: str(trig.type) ?? '',
      where: arr(trig.where).map(condition),
      schedule: str(trig.type) === SCHEDULE_TRIGGER ? (schedule(trig.schedule) ?? defaultSchedule()) : null,
    },
    target: { scope, ids: arr(target.ids).map(String), project_ids: arr(target.project_ids).map(String) },
    conditions: arr(o.conditions).map(condition),
    steps: phases.length ? [] : arr(o.steps).map(step),
    phases,
    cooldown: { per: cooldown.per === 'flow' ? 'flow' : 'subject', seconds: num(cooldown.seconds) ?? 0 },
  };
}

export function emptyFlow(): FlowDef {
  return {
    trigger: { type: '', where: [] },
    target: { scope: 'all', ids: [], project_ids: [] },
    conditions: [],
    steps: [],
    phases: [],
    cooldown: { per: 'subject', seconds: 0 },
  };
}

/** Only the fields that mean something for each step's type (a readable stored document). */
function stepToServer(s: Step): Record<string, unknown> {
  const out: Record<string, unknown> = { id: s.id, type: s.type };
  if (s.label) out.label = s.label;
  const put = (k: string, v: unknown) => {
    if (v !== null && v !== undefined && v !== '') out[k] = v;
  };
  switch (s.type) {
    case 'action':
      put('action', s.action);
      out.with = cleanInput(s.with ?? {});
      break;
    case 'wait':
      put('event', s.event);
      if (s.match?.length) out.match = s.match;
      put('timeout_s', s.timeout_s);
      if (s.on_timeout && s.on_timeout !== 'continue') out.on_timeout = s.on_timeout;
      if (s.on_timeout === 'goto') put('timeout_goto', s.timeout_goto);
      break;
    case 'delay':
      put('seconds', s.seconds);
      break;
    case 'approval':
    case 'task':
      put('title', s.title);
      put('prompt', s.prompt);
      put('permission', s.permission);
      put('assignee', s.assignee);
      put('due_s', s.due_s);
      if (s.type === 'task') put('evidence_event', s.evidence_event);
      break;
    case 'ai':
      put('prompt', s.prompt);
      out.output = s.output ?? 'text';
      break;
    case 'branch':
      out.if = s.if ?? [];
      put('then', s.then);
      put('else_', s.else); // the server's key for "else" (definition.py has no alias for it)
      break;
  }
  return out;
}

/** Drop inputs the author left empty, so the action's own defaults apply. */
export function cleanInput(input: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(input).filter(([, v]) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0)));
}

export function toServer(def: FlowDef): Record<string, unknown> {
  const trigger: Record<string, unknown> = { type: def.trigger.type, where: def.trigger.where };
  if (def.trigger.type === SCHEDULE_TRIGGER && def.trigger.schedule) trigger.schedule = scheduleToServer(def.trigger.schedule);
  const out: Record<string, unknown> = {
    trigger,
    target: def.target,
    conditions: def.conditions,
    cooldown: def.cooldown,
  };
  if (def.phases.length) out.phases = def.phases.map((p) => ({ id: p.id, label: p.label, steps: p.steps.map(stepToServer) }));
  else out.steps = def.steps.map(stepToServer);
  return out;
}

export function allSteps(def: FlowDef): Step[] {
  return def.phases.length ? def.phases.flatMap((p) => p.steps) : def.steps;
}

/** Where a step lives: `steps.3` or `phases.1.steps.0` (the server's validation locations). */
export function stepLoc(def: FlowDef, id: string): string | null {
  if (def.phases.length) {
    for (const [pi, p] of def.phases.entries()) {
      const si = p.steps.findIndex((s) => s.id === id);
      if (si >= 0) return `phases.${pi}.steps.${si}`;
    }
    return null;
  }
  const i = def.steps.findIndex((s) => s.id === id);
  return i >= 0 ? `steps.${i}` : null;
}

/** A readable, unique id for a new step: `request_video`, `approval_2`. */
export function newStepId(def: FlowDef, base: string): string {
  const clean =
    base
      .toLowerCase()
      .replace(/[^a-z0-9_]+/g, '_')
      .replace(/^[^a-z]+/, '')
      .slice(0, 34) || 'step';
  const taken = new Set(allSteps(def).map((s) => s.id));
  if (!taken.has(clean)) return clean;
  for (let n = 2; n < 1000; n++) if (!taken.has(`${clean}_${n}`)) return `${clean}_${n}`;
  return `${clean}_${Date.now() % 100000}`;
}

export function newStep(def: FlowDef, type: StepType, action?: string): Step {
  const base = type === 'action' && action ? (action.split('.').pop() ?? 'action') : type;
  const id = newStepId(def, base);
  switch (type) {
    case 'action':
      return { id, type, action: action ?? null, with: {} };
    case 'wait':
      return { id, type, event: null, match: [], timeout_s: 3600, on_timeout: 'continue' };
    case 'delay':
      return { id, type, seconds: 600 };
    case 'approval':
      return { id, type, title: '', permission: null, assignee: null, due_s: 86400 };
    case 'task':
      return { id, type, title: '', permission: null, assignee: null, due_s: 86400 };
    case 'ai':
      return { id, type, prompt: '', output: 'text' };
    case 'branch':
      return { id, type, if: [], then: null, else: null };
  }
}

/** Apply `fn` to the step list that holds step `id` (top level or inside a phase). */
function withList(def: FlowDef, id: string, fn: (list: Step[], index: number) => Step[]): FlowDef {
  if (def.phases.length) {
    return {
      ...def,
      phases: def.phases.map((p) =>
        p.steps.some((s) => s.id === id)
          ? {
              ...p,
              steps: fn(
                p.steps,
                p.steps.findIndex((s) => s.id === id),
              ),
            }
          : p,
      ),
    };
  }
  const i = def.steps.findIndex((s) => s.id === id);
  return i < 0 ? def : { ...def, steps: fn(def.steps, i) };
}

export function updateStep(def: FlowDef, id: string, patch: Partial<Step>): FlowDef {
  return withList(def, id, (list, i) => list.map((s, j) => (j === i ? { ...s, ...patch } : s)));
}

export function moveStep(def: FlowDef, id: string, dir: -1 | 1): FlowDef {
  return withList(def, id, (list, i) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return list;
    const next = [...list];
    const a = next[i] as Step;
    next[i] = next[j] as Step;
    next[j] = a;
    return next;
  });
}

const REF = (id: string) => new RegExp(`\\{\\{\\s*steps\\.${id}\\.`, 'g');

function mapStrings(v: unknown, fn: (s: string) => string): unknown {
  if (typeof v === 'string') return fn(v);
  if (Array.isArray(v)) return v.map((x) => mapStrings(x, fn));
  if (v && typeof v === 'object') return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, mapStrings(x, fn)]));
  return v;
}

/** Remove a step and every reference to it (branch targets, timeout jumps). */
export function removeStep(def: FlowDef, id: string): FlowDef {
  const cleared = withList(def, id, (list, i) => list.filter((_, j) => j !== i));
  const fix = (s: Step): Step => ({
    ...s,
    then: s.then === id ? null : s.then,
    else: s.else === id ? null : s.else,
    timeout_goto: s.timeout_goto === id ? null : s.timeout_goto,
  });
  return cleared.phases.length
    ? { ...cleared, phases: cleared.phases.map((p) => ({ ...p, steps: p.steps.map(fix) })) }
    : { ...cleared, steps: cleared.steps.map(fix) };
}

/** Rename a step and follow every reference to it ({{steps.<id>.…}}, branch targets). */
export function renameStep(def: FlowDef, from: string, to: string): FlowDef {
  if (from === to) return def;
  const fix = (s: Step): Step => {
    const next = mapStrings(s, (x) => x.replace(REF(from), `{{steps.${to}.`)) as Step;
    return {
      ...next,
      id: s.id === from ? to : s.id,
      then: s.then === from ? to : s.then,
      else: s.else === from ? to : s.else,
      timeout_goto: s.timeout_goto === from ? to : s.timeout_goto,
    };
  };
  return def.phases.length ? { ...def, phases: def.phases.map((p) => ({ ...p, steps: p.steps.map(fix) })) } : { ...def, steps: def.steps.map(fix) };
}

export function addStep(def: FlowDef, step: Step, phaseId?: string): FlowDef {
  if (def.phases.length) {
    const target = phaseId ?? def.phases[def.phases.length - 1]?.id;
    return { ...def, phases: def.phases.map((p) => (p.id === target ? { ...p, steps: [...p.steps, step] } : p)) };
  }
  return { ...def, steps: [...def.steps, step] };
}

/** Case flows: put the steps into a first phase, or flatten the phases back into one list. */
export function togglePhases(def: FlowDef, on: boolean, firstLabel: string): FlowDef {
  if (on && !def.phases.length) return { ...def, phases: [{ id: 'phase_1', label: firstLabel, steps: def.steps }], steps: [] };
  if (!on && def.phases.length) return { ...def, steps: def.phases.flatMap((p) => p.steps), phases: [] };
  return def;
}

export function addPhase(def: FlowDef, label: string): FlowDef {
  const taken = new Set(def.phases.map((p) => p.id));
  let n = def.phases.length + 1;
  while (taken.has(`phase_${n}`)) n++;
  return { ...def, phases: [...def.phases, { id: `phase_${n}`, label, steps: [] }] };
}

export function removePhase(def: FlowDef, phaseId: string): FlowDef {
  const phase = def.phases.find((p) => p.id === phaseId);
  if (!phase) return def;
  const rest = def.phases.filter((p) => p.id !== phaseId);
  if (!rest.length) return { ...def, phases: [], steps: phase.steps };
  // its steps move to the previous phase (or the next one), never lost
  const idx = Math.max(0, def.phases.indexOf(phase) - 1);
  return { ...def, phases: rest.map((p, i) => (i === idx ? { ...p, steps: [...p.steps, ...phase.steps] } : p)) };
}

/** A string that is exactly one reference (`{{trigger.vehicle_id}}`), or null. */
export function refOf(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const m = /^\s*\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}\s*$/.exec(v);
  return m ? (m[1] ?? null) : null;
}

export const hasRef = (v: unknown): boolean => (typeof v === 'string' ? /\{\{\s*[a-zA-Z0-9_.]+\s*\}\}/.test(v) : Array.isArray(v) ? v.some(hasRef) : false);
